import { Frame, Head, Label, fx } from './frame';
import { useStrings } from '../../../state/useLocale';
import type { FigureProps } from './index';

/**
 * The three shell figures: where you are standing, where output goes, and what
 * a recursive search actually walks.
 *
 * All three are about the same misconception, which is that a command acts on
 * "the project". It does not: it acts from one folder, it prints to one place
 * unless told otherwise, and it reads exactly the files it was pointed at. So
 * each drawing puts the mechanism somewhere you can point at it — a marker in
 * the gutter beside the folder the shell is in, two physical paths out of one
 * command, a scanner stepping down a list of files one at a time.
 *
 * Signal Green is spent once in each: the prompt that says where you stand,
 * the `>` that diverts the output and the line it diverts along, the word that
 * matched. Nothing else in these drawings is coloured.
 *
 * Almost every word in these three is the machine's, and stays: `workspace`,
 * `workstation`, `docs`, `handbook.md` and `notes.md` are the guest's actual
 * folders (`cwd: workstation` in the missions), `grep -r "total" .` is typed,
 * and the matched line is printed. What is translated is the four sentences
 * the author wrote about them. See frame.tsx, `Label`.
 */

/* ── filesystem ──────────────────────────────────────────────────────────────
   A folder tree with the shell standing in one of its folders. The claim is
   that `docs/handbook.md` is not an address in the project, it is an address
   from here: the bright trace starts at the folder the green prompt marks and
   ends at the file the label names. */

export function FilesystemFigure({ active }: FigureProps) {
  const f = useStrings().warmup.fig;

  return (
    <Frame name="filesystem" active={active}>
      <g className="fx-in" style={fx(0)}>
        <text x={40} y={28}>
          workspace
        </text>
      </g>

      <g className="fx-rise" style={fx(70)}>
        <path className="rule" d="M30 33 V49 H52" />
        <text className="lbl-ink" x={56} y={52}>
          workstation
        </text>
      </g>

      <g className="fx-rise" style={fx(140)}>
        <path className="rule" d="M46 57 V121 M46 73 H68" />
        <text x={72} y={76}>
          docs
        </text>
      </g>

      <g className="fx-rise" style={fx(210)}>
        <path className="rule" d="M62 81 V97 H84" />
        <text x={88} y={100}>
          handbook.md
        </text>
      </g>

      <g className="fx-rise" style={fx(280)}>
        <path className="rule" d="M46 121 H68" />
        <text x={72} y={124}>
          notes.md
        </text>
      </g>

      {/* The shell itself, in the gutter, beside the one folder it is in. */}
      <text className="lbl-sig glyph fx-rise" style={fx(380, { '--dx': '-10px', '--dy': '0px' })} x={8} y={54}>
        $
      </text>

      {/* The path, traced along the tree it is relative to. */}
      <path
        className="wire bright fx-draw"
        style={fx(560, { '--t': '340ms', '--len': 96 })}
        d="M46 57 V73 H68 M62 81 V97 H84"
      />

      <path className="wire ghost fx-in" style={fx(880)} d="M156 96.5 H164" />
      <text className="lbl-ink fx-in" style={fx(900)} x={168} y={100}>
        docs/handbook.md
      </text>
      <Label className="lbl-dim fx-in" style={fx(1000)} x={168} y={116} at="left">
        {f.fromWhereYouStand}
      </Label>
    </Frame>
  );
}

/* ── redirect ────────────────────────────────────────────────────────────────
   One command, two possible destinations. The output goes to the screen once,
   the way it always has, and then the `>` arrives and takes the same output
   down the other path instead. The screen keeps a ghost of what it would have
   printed, because that is what the learner will be surprised by: nothing
   appears, and nothing is wrong. */

export function RedirectFigure({ active }: FigureProps) {
  const f = useStrings().warmup.fig;

  return (
    <Frame name="redirect" active={active}>
      <g className="fx-rise" style={fx(0)}>
        <rect className="box" x={12} y={56} width={94} height={30} rx={2} />
        <text className="lbl-ink" x={20} y={75}>
          echo ready
        </text>
      </g>

      {/* The screen. */}
      <Label className="lbl-dim fx-in" style={fx(120)} x={182} y={18} at="left">
        {f.screen}
      </Label>
      <rect className="box fx-in" style={fx(120)} x={182} y={24} width={124} height={38} rx={2} />

      {/* The route that was taken once and is not taken any more. The dashed
          line underneath is the resting state; the bright one over it and the
          text on the screen flash and recede on the same beat. */}
      <path className="wire ghost fx-in" style={fx(1040)} d="M106 71 C136 71, 146 43, 170 43" />
      <Head className="ghost fx-in" style={fx(1040)} x={178} y={43} dir="right" />
      <path
        className="wire bright fx-flash"
        style={fx(150, { '--t': '900ms' })}
        d="M106 71 C136 71, 146 43, 170 43"
        opacity={0}
      />
      <Head className="bright fx-flash" style={fx(150, { '--t': '900ms' })} x={178} y={43} dir="right" opacity={0} />
      <text className="lbl-ink fx-flash" style={fx(330, { '--t': '860ms' })} x={192} y={48} opacity={0.3}>
        ready
      </text>

      {/* The route the `>` opens. The glyph sits under the line it opens, not
          on it: the curve is already at y 87 by the time it reaches here. */}
      <text className="lbl-sig glyph fx-pop" style={fx(560)} x={126} y={104}>
        &gt;
      </text>
      <path
        className="wire sig fx-draw"
        style={fx(640, { '--t': '300ms', '--len': 120 })}
        d="M106 75 C134 75, 142 108, 170 108"
      />
      <Head className="sig fx-in" style={fx(920)} x={178} y={108} dir="right" />

      {/* The file, drawn as a page so it is not mistaken for another box. */}
      <g className="fx-rise" style={fx(940, { '--dx': '-8px' })}>
        <path className="box" d="M182 88 H296 L306 98 V126 H182 Z" />
        <path className="rule" d="M296 88 V98 H306" />
        <text className="lbl-ink" x={192} y={112}>
          ready.txt
        </text>
      </g>

      <Label className="lbl-dim fx-in" style={fx(1080)} x={12} y={134} at="left">
        {f.redirectSends}
      </Label>
    </Frame>
  );
}

/* ── search ──────────────────────────────────────────────────────────────────
   `grep -r` is a walk, not a lookup. The scanner steps down the file list one
   file per step — the only stepped animation in the set, because the thing it
   is describing is discrete — and stops on the file that matched. The word
   that matched is the only green in the drawing. */

export function SearchFigure({ active }: FigureProps) {
  const f = useStrings().warmup.fig;
  const files = [
    { name: 'cart.js', y: 44, match: false },
    { name: 'notes.md', y: 64, match: false },
    { name: 'checkout.js', y: 84, match: false },
    { name: 'pricing.js', y: 104, match: true },
  ];

  return (
    <Frame name="search" active={active}>
      <text className="lbl-ink fx-in" style={fx(0)} x={14} y={24}>
        {'grep -r "total" .'}
      </text>

      {files.map((file, i) => (
        <g key={file.name} className="fx-rise" style={fx(80 + i * 60)}>
          <rect className="box" x={16} y={file.y - 8} width={7} height={10} rx={1} />
          <text className={file.match ? 'lbl-ink' : 'lbl-dim'} x={30} y={file.y}>
            {file.name}
          </text>
        </g>
      ))}

      {/* The walk. Four files, three steps, ending on the one that matched. */}
      <Head
        className="bright fx-walk"
        style={fx(300, { '--t': '540ms', '--dy': '-60px', '--n': 3 })}
        x={12}
        y={104}
        dir="right"
      />

      <path
        className="wire bright fx-draw"
        style={fx(860, { '--t': '220ms', '--len': 80 })}
        d="M96 100 C120 100, 128 90, 148 90"
      />

      {/* The matched line, in four pieces rather than one line of tspans: the
          word that matched has a beat of its own, and only a text element can
          carry an opacity a browser will honour. */}
      <rect className="well fx-in" style={fx(880)} x={152} y={76} width={154} height={28} rx={2} />
      <g className="fx-in" style={fx(940)}>
        <text className="lbl-dim" x={160} y={94}>
          pricing.js:
        </text>
        <text className="lbl-ink" x={228} y={94}>
          const
        </text>
        <text className="lbl-ink" x={296} y={94}>
          =
        </text>
      </g>
      <text className="lbl-sig fx-in" style={fx(1040)} x={262} y={94}>
        total
      </text>

      <Label className="lbl-dim fx-in" style={fx(1100)} x={14} y={132} at="left">
        {f.recursiveWalk}
      </Label>
    </Frame>
  );
}
